import {
  all, call, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';
import { get } from 'lodash';
import callAdapter, { callAdapterResponseError } from '@/utils/sagaHelper';
import findResult, {
  isValidFilter, removeEmpty, successfulAssociation, wildcardFilter,
} from '@/utils/resultsHelper';
import selectors from '@/selectors';
import CollectionDatasetsService from '@/collections/datasets/service';
import { fetchCollectionDatasets } from '@/collections/datasets/actions';
import {
  showDatasetCollectionsError,
  showDatasetCollections,
  removeDatasetCollectionError,
  removeDatasetCollectionSuccess,
  fetchDatasetCollectionsAssignableSuccess,
  fetchDatasetCollectionsAssignableError,
  addDatasetCollectionSuccess,
  addDatasetCollectionsError,
} from '../actions';
import datasetCollectionActions from '../constants';
import DatasetCollectionsService from '../service';

function* refreshCollectionDatasets(collections) {
  const collectionDatasets = yield select(selectors.collectionDatasets);
  const collectionsToRefresh = removeEmpty(collections.map((collection) => ((
    findResult(collectionDatasets, 'open', collection.code)
  ))));
  yield all(collectionsToRefresh.map((collection) => put(fetchCollectionDatasets('open', collection.code))));
}

export function* datasetCollections({ category, code }) {
  const { response, error } = yield call(callAdapter, DatasetCollectionsService.collections(code));
  if (error) {
    yield put(showDatasetCollectionsError(category, code, error));
  } else {
    yield put(showDatasetCollections(
      category,
      code,
      get(response.additional, 'dataset.catalogue.title', ''),
      response,
    ));
  }
}

export function* removeDatasetCollection({
  category, code, collectionCode, name,
}) {
  const item = { code: collectionCode };
  const { error } = yield call(callAdapterResponseError, item, CollectionDatasetsService.removeDataset(item, code));

  if (error) {
    yield put(removeDatasetCollectionError(category, code, collectionCode, error, name));
  } else {
    yield put(removeDatasetCollectionSuccess(category, code, collectionCode, name));
    yield refreshCollectionDatasets([item]);
  }
}

export function* datasetCollectionsAssignable({ category, code, filter }) {
  const items = yield select(selectors.datasetCollections);
  const item = findResult(items, category, code);

  const filterToCheck = filter || item.unassignedCollections.filter;
  if (!isValidFilter(filterToCheck, item.unassignedCollections)) { return; }

  const { response, error } = yield call(callAdapter, DatasetCollectionsService.collections(
    code,
    wildcardFilter(filterToCheck),
    true,
  ));

  if (error) {
    yield put(fetchDatasetCollectionsAssignableError(category, code, error));
  } else {
    yield put(fetchDatasetCollectionsAssignableSuccess(category, code, response.items, response.paging.total));
  }
}

export function* addDatasetCollections() {
  const { category, code, name } = yield select(selectors.collectionAssigningDataset);
  const items = yield select(selectors.datasetCollections);
  const datasetCollectionResult = findResult(items, category, code);
  const results = yield all(datasetCollectionResult.collectionsToAdd.results.map(
    (i) => callAdapterResponseError(i, CollectionDatasetsService.addDataset(i, code)),
  ));
  const [success, errors] = results.reduce((result, element) => {
    result[successfulAssociation(element, 'Collection not found')
    || successfulAssociation(element, 'Dataset not found') ? 0 : 1].push(element);
    return result;
  }, [[], []]);

  if (errors.length > 0) {
    yield put(addDatasetCollectionsError(errors));
  }
  if (success.length > 0) {
    yield put(addDatasetCollectionSuccess(code, name, success));
    yield call(datasetCollections, { category, code });
    yield refreshCollectionDatasets(success);
  }
}

export default function* datasetCollectionsApi() {
  yield takeEvery(datasetCollectionActions.FETCH_DATASET_COLLECTIONS, datasetCollections);
  yield takeEvery(datasetCollectionActions.FETCH_DATASET_COLLECTIONS_ASSIGNABLE, datasetCollectionsAssignable);
  yield takeEvery(datasetCollectionActions.REMOVE_DATASET_COLLECTION, removeDatasetCollection);
  yield takeLatest(datasetCollectionActions.ADD_DATASET_COLLECTIONS, addDatasetCollections);
}
